import type { ComponentProps, MouseEvent, MouseEventHandler } from 'react'
import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import Step from './Step'
import StepConnector from './StepConnector'
import _StepperProgressContext from './_StepperProgressContext'
import { centreElementInView } from 'lib/window/scrollUtils'

type StepProps = ComponentProps<typeof Step>

type InheritedStepProps = Pick<StepProps, 'label' | 'sublabel'>

export type StepClickHandler = (index: number, event?: MouseEvent<HTMLButtonElement>) => void

interface Props extends InheritedStepProps {
  stepIndex: number
  onStepClick?: StepClickHandler
}

const StepperProgressStep = forwardRef<HTMLButtonElement, Props>(function StepperProgressStep(props, ref) {
  const { stepIndex, onStepClick, ...rest } = props
  const stepRef = useRef<HTMLButtonElement>(null)
  const state = useContext(_StepperProgressContext)
  const { activeStepIndex, trackElement } = state

  const stepState = useMemo<StepProps['state']>(() => {
    if (stepIndex === activeStepIndex) return 'active'
    if (activeStepIndex !== undefined && stepIndex < activeStepIndex) return 'completed'
    return 'inactive'
  }, [activeStepIndex, stepIndex])

  const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    onStepClick?.(stepIndex, event)
  }, [onStepClick, stepIndex])

  useEffect(() => {
    if (stepState === 'active' && stepRef.current && trackElement) {
      centreElementInView(stepRef.current, trackElement)
    }
  }, [stepState, trackElement])

  useImperativeHandle(ref, () => stepRef.current!)

  return <>
    {stepIndex !== 0 && <StepConnector />}
    <Step
      {...rest}
      ref={stepRef}
      number={stepIndex + 1}
      state={stepState}
      interactive={!!onStepClick}
      onClick={handleClick}
    />
  </>
})

export default StepperProgressStep
